/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState } from 'react'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'

import ImportCSVContainer from '../ImportCSVContainer'
import ImportCSVTitle from '../ImportCSVTitle'
import ImportCSVBanner, { BANNER_TYPES } from '../ImportCSVBanner'
import ImportCSVContentUploadForm from '../ImportCSVContentUploadForm'
import ImportTeamRosterErrorDuplicates from './ImportTeamRosterErrorDuplicates'
import ImportCSVError from '../ImportCSVError'
import { useBeforeUnload } from '../../../hooks/useBeforeUnload'
import { decodeCsvFile } from './utils/decodeCsvFile'
import { importCsvLayout } from './utils/importCsvRows'
import { importRequestOutcome } from './utils/importRequestOutcome'
import ImportCSVFailedRowsLink from '../ImportCSVFailedRowsLink'

/**
 * Body of the IMPORT ROSTER modal. Mounted by `ImportTeamRoster`, and directly
 * by callers that supply their own trigger button.
 *
 * `orgId` / `isMultiTeamRoster` apply only to the Org screen's multi-team
 * import — the JSDoc makes that optionality visible to TypeScript callers,
 * which bare destructuring would otherwise infer as required.
 *
 * @param {Object} props
 * @param {boolean} [props.loading]
 * @param {(loading: boolean) => void} [props.setLoading]
 * @param {any} [props.importTemplate] Entry from `getConfig()`.
 * @param {string} [props.teamId]
 * @param {string} [props.teamName]
 * @param {string} [props.teamRosterId]
 * @param {() => void} [props.toggle] Closes the modal.
 * @param {() => void} [props.onSuccess]
 * @param {string} [props.orgId] Multi-team import only; omit for one team.
 * @param {boolean} [props.isMultiTeamRoster]
 * @param {'players' | 'teamOfficials'} [props.rosterType] Which roster the file fills.
 */
const ImportTeamRosterContent = ({
  loading,
  setLoading,
  importTemplate,
  teamId,
  teamName,
  teamRosterId,
  toggle,
  onSuccess = () => {},
  orgId,
  isMultiTeamRoster = false,
  rosterType = 'players',
}) => {
  const [success, setSuccess] = useState(false)
  const [partialSuccess, setPartialSuccess] = useState(false)
  const [importError, setImportError] = useState(false)
  const [file, setFile] = useState(null)
  const [error, setError] = useState(false)
  // The file the last upload sent and its decoded rows: the duplicate review
  // posts this same file, and reads these rows for its labels and rewrite.
  const [uploadedCsv, setUploadedCsv] = useState(null)
  const [shouldCheckForConflicts, setShouldCheckForConflicts] = useState(true)
  const [hasConflicts, setHasConflicts] = useState(false)
  const [errorCSV, setErrorCSV] = useState(false)
  const [clearRosterBeforeImport, setClearRosterBeforeImport] = useState(false)
  // Set before the upload goes out, so a second submit event sends nothing.
  const uploadInFlight = useRef(false)
  // Set when an upload ended without an answer: it may have imported. Only
  // choosing a file again clears it.
  const uploadOutcomeUnknown = useRef(false)

  // Either request (this upload, or the review's confirm) may still be writing.
  useBeforeUnload(loading)

  const hasImportErrors = Array.isArray(importError) && importError.length > 0
  const isTeamOfficials = rosterType === 'teamOfficials'
  const submitUrl =
    teamId && teamRosterId
      ? `/teams/${teamId}/rosters/${teamRosterId}/${
          isTeamOfficials ? 'team-officials' : 'players'
        }/import/csv`
      : `/organizations/${orgId}/teams/${
          isTeamOfficials ? 'team-officials/importmulti' : 'importmulti'
        }/csv`

  useEffect(() => {
    return () => {
      setLoading(false)

      setPartialSuccess(false)
      setSuccess(false)
      setImportError(false)
      setFile(false)
      setError(false)
      setUploadedCsv(null)
      setShouldCheckForConflicts(true)
      setHasConflicts(false)
      setErrorCSV(false)
    }
  }, [])

  const onUploadComplete = (response) => {
    setImportError(false)
    setHasConflicts(false)
    setSuccess(response)
    setLoading(false)
    onSuccess()
    setTimeout(toggle, 10000)
  }

  // A 409 from either pass: the rows to review, or the rows that failed.
  const handleImportError = (e) => {
    setLoading(false)

    if (
      Object.prototype.hasOwnProperty.call(e, 'failed') &&
      e.failed.count > 0
    ) {
      // Row errors replace whatever was on screen — including a
      // duplicate review from the previous pass, which otherwise kept
      // rendering and hid the errors (the user was stuck).
      setHasConflicts(false)
      setErrorCSV(e.file)
      setImportError(e?.failed?.errors)
    } else if (
      Object.prototype.hasOwnProperty.call(e, 'conflicts') &&
      e.conflicts.count > 0
    ) {
      setErrorCSV(false)
      setHasConflicts(true)
      setImportError(e?.conflicts?.conflicts)
    }

    if (e.success?.count > 0) {
      setPartialSuccess(e.success.count)
    }
    // We specifically call the success handler here in the case of error, because
    // sometimes the API will report an error but still process an upload? I know.
    onSuccess()
    setFile(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    if (!file || uploadInFlight.current || uploadOutcomeUnknown.current) {
      return
    }

    uploadInFlight.current = true
    setError(false)
    setPartialSuccess(false)
    setErrorCSV(false)
    setLoading(true)

    try {
      let rows
      try {
        rows = await decodeCsvFile(file)
      } catch (readError) {
        setError(
          `There was a problem reading your file | ${readError?.message}`
        )
        return
      }
      setUploadedCsv({ file, rows })

      const body = new FormData()
      body.append('file', file)
      body.append('conflicts', shouldCheckForConflicts ? 'check' : 'ignore')
      body.append('clear_roster', clearRosterBeforeImport)

      try {
        const response = await req(submitUrl, {
          body,
          method: 'POST',
          parseJSON: false,
          skipAutoContentType: true,
          // One attempt: with no rows to review this request imports the file,
          // so a replay after a lost response would import it twice.
          maxAttempts: 1,
          reportGlobalError: false,
        })

        onUploadComplete(response)
      } catch (requestError) {
        const outcome = importRequestOutcome(requestError)

        if (outcome === 'report') {
          handleImportError(requestError)
        } else if (outcome === 'unknown') {
          uploadOutcomeUnknown.current = true
          setFile(null)
          setError(t('common:importDuplicatesOutcomeUnknown'))
        } else {
          setError(
            `There was a problem importing your file | ${
              requestError?.message || JSON.stringify(requestError)
            }`
          )
        }
      }
    } finally {
      uploadInFlight.current = false
      setLoading(false)
    }
  }

  const onFileSelect = ({ target }) => {
    // A file dropped while a request runs must not replace the one it sent.
    if (
      !target ||
      !target.files ||
      target.files.length === 0 ||
      uploadInFlight.current
    ) {
      return
    }

    const file = target.files[0]

    if (
      /image\/svg\+xml/.test(file?.type) ||
      (!/csv/.test(file?.type) && !/\.csv$/.test(file?.name))
    ) {
      setError('Please select a CSV file.')
      setTimeout(() => {
        setError(false)
      }, 5000)
      return
    }

    uploadOutcomeUnknown.current = false
    setError(false)
    setImportError(false)
    setPartialSuccess(false)
    setErrorCSV(false)
    setFile(file)
  }

  return (
    <ImportCSVContainer hasImportErrors={hasImportErrors}>
      <ImportCSVTitle
        importTemplate={importTemplate}
        success={success}
        onClose={toggle}
        closeDisabled={loading}
        title={hasConflicts ? 'Duplicate Review' : undefined}
      />
      <div role='status'>
        {loading && !hasConflicts && uploadedCsv ? (
          <ImportCSVBanner
            type={BANNER_TYPES.info.type}
            message={t('common:importDuplicatesImporting', {
              count: importCsvLayout(uploadedCsv.rows).dataRows.length,
            })}
          />
        ) : null}
      </div>
      {typeof partialSuccess === 'number' && (
        <ImportCSVBanner
          type={BANNER_TYPES.success.type}
          message={`${partialSuccess} item${
            partialSuccess > 1 ? 's were' : ' was'
          } imported successfully.`}
        />
      )}
      {error && (
        <ImportCSVBanner type={BANNER_TYPES.error.type} message={error} />
      )}
      {errorCSV && (
        <ImportCSVFailedRowsLink csv={errorCSV.rows} fileName={errorCSV.name} />
      )}
      {hasImportErrors ? (
        hasConflicts ? (
          <ImportTeamRosterErrorDuplicates
            conflicts={importError}
            teamName={teamName}
            csvRows={uploadedCsv?.rows ?? []}
            file={uploadedCsv?.file}
            resetFile={() => {
              setError(false)
              setImportError(false)
              setFile(null)
              setHasConflicts(false)
              setShouldCheckForConflicts(true)
              setUploadedCsv(null)
            }}
            submitUrl={submitUrl}
            onUploadComplete={onUploadComplete}
            onImportError={handleImportError}
            setLoading={setLoading}
            isMultiTeamRoster={isMultiTeamRoster}
          />
        ) : (
          <>
            <ImportCSVBanner
              type={BANNER_TYPES.error.type}
              message={BANNER_TYPES.error.message}
            />
            <ImportCSVError errors={importError} />
          </>
        )
      ) : (
        false
      )}
      {success ? (
        <>
          <ImportCSVBanner
            type={BANNER_TYPES.success.type}
            message={
              'Your team roster update was completed successfully. Dismissing this modal in 10 seconds...'
            }
          />
          <div
            css={css`
              border: 1px solid #3f4753;
              border-radius: 4px;
              padding: 10px;
            `}
          >
            {success?.success?.count} player(s) were imported.
          </div>
        </>
      ) : (
        !hasConflicts && (
          <ImportCSVContentUploadForm
            onSubmit={onSubmit}
            loading={loading}
            onFileSelect={onFileSelect}
            resetFile={() => setFile(null)}
            file={file}
            importTemplate={importTemplate}
            error={error}
            clearRosterBeforeImport={clearRosterBeforeImport}
            setClearRosterBeforeImport={setClearRosterBeforeImport}
          />
        )
      )}
    </ImportCSVContainer>
  )
}

export default ImportTeamRosterContent
